import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

type Props = {
  readonly candidateId: Id<'candidates'>
}

const formatDueDate = (dueAt: number): string => {
  return new Date(dueAt).toLocaleDateString()
}

const isOverdue = (
  dueAt: number | undefined,
  completedAt: number | undefined,
): boolean => {
  if (completedAt !== undefined) return false
  if (dueAt === undefined) return false
  return dueAt < Date.now()
}

export const CandidateTodosPanel = ({ candidateId }: Props) => {
  const [newTitle, setNewTitle] = useState('')

  const { data: currentUser } = useQuery(
    convexQuery(api.features.user.user.getMyCurrentUser, {}),
  )

  const { data: todos = [] } = useQuery(
    convexQuery(api.features.candidates.candidate_todos.listMyCandidateTodos, {
      candidateId,
    }),
  )

  const { mutateAsync: createTodo } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidate_todos.createMyCandidateTodo,
    ),
  })

  const { mutateAsync: completeTodo } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidate_todos.completeMyCandidateTodo,
    ),
  })

  const { mutateAsync: uncompleteTodo } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidate_todos.uncompleteMyCandidateTodo,
    ),
  })

  const { mutateAsync: deleteTodo } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidate_todos.deleteMyCandidateTodo,
    ),
  })

  const handleCreateTodo = async (): Promise<void> => {
    const trimmed = newTitle.trim()
    if (!trimmed || !currentUser) return
    await createTodo({
      candidateId,
      assignedToUserId: currentUser._id,
      title: trimmed,
    })
    setNewTitle('')
  }

  const handleToggleComplete = async (
    todoId: Id<'candidateTodos'>,
    completedAt: number | undefined,
  ): Promise<void> => {
    if (completedAt !== undefined) {
      await uncompleteTodo({ todoId })
    } else {
      await completeTodo({ todoId })
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium text-primary">To-dos</h3>

      {todos.length === 0 ? (
        <p className="text-xs text-primary/40">No to-dos yet.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {todos.map((todo) => {
            const overdue = isOverdue(todo.dueAt, todo.completedAt)
            return (
              <li
                key={todo._id}
                className={cn(
                  'flex items-start justify-between gap-2 rounded-md border px-3 py-2',
                  overdue ? 'border-red-300 bg-red-50' : 'border-border',
                )}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
                    checked={todo.completedAt !== undefined}
                    onChange={() =>
                      void handleToggleComplete(
                        todo._id as Id<'candidateTodos'>,
                        todo.completedAt,
                      )
                    }
                  />
                  <div className="flex flex-col gap-0.5">
                    <span
                      className={cn(
                        'text-sm',
                        todo.completedAt !== undefined
                          ? 'text-primary/40 line-through'
                          : 'text-primary',
                      )}
                    >
                      {todo.title}
                    </span>
                    {todo.dueAt !== undefined && (
                      <span
                        className={cn(
                          'text-xs',
                          overdue ? 'text-red-600' : 'text-primary/50',
                        )}
                      >
                        Due {formatDueDate(todo.dueAt)}
                        {overdue && ' · Overdue'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="shrink-0 text-xs text-primary/40 hover:text-red-600"
                  onClick={() =>
                    void deleteTodo({
                      todoId: todo._id as Id<'candidateTodos'>,
                    })
                  }
                >
                  Delete
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="flex gap-2">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Add a to-do..."
          className="text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleCreateTodo()
          }}
        />
        <Button
          size="sm"
          onClick={() => void handleCreateTodo()}
          disabled={!newTitle.trim()}
        >
          Add
        </Button>
      </div>
    </div>
  )
}
